import {
  Fragment,
  isValidElement,
  useEffect,
  useState,
  type KeyboardEvent,
  type MouseEvent,
  type ReactNode,
} from 'react';
import { Checkbox } from '../atoms/Checkbox';
import { Icon } from '../atoms/Icon';
import { Skeleton } from '../atoms/Skeleton';
import { compactQuery, useMediaQuery } from '../hooks/useMediaQuery';
import { orderRowActions, RowActions, useRowActions } from '../molecules/RowActions';
import type { MenuItem } from '../molecules/Menu';
import { useLinkComponent } from '../navigation';
import styles from './DataTable.module.css';

export type SortDirection = 'ascending' | 'descending';
export interface TableSort {
  key: string;
  direction: SortDirection;
}

/** Clicks on these do their own thing instead of opening the row. */
const INTERACTIVE = 'a, button, input, select, textarea, label, summary, [role="button"]';
/** Row menus and dialogs live inside the row in the DOM, but are not the row. */
const OVERLAY = '[role="menu"], dialog';

export interface DataTableColumn<T> {
  key: string;
  header: string;
  /** Header only for screen readers, e.g. the row labels of a matrix. */
  hideHeader?: boolean;
  cell: (row: T) => ReactNode;
  /** `center` for controls in a matrix (checkboxes, switches). */
  align?: 'start' | 'center' | 'end';
  /**
   * Value to sort by: the header becomes a sort button and the table sorts
   * its rows itself (empty values last). Without it, `sortable` shows the
   * button and the caller sorts in `onSortChange`.
   */
  sortValue?: (row: T) => string | number | null | undefined;
  /** Shows a sort button in the header; the caller sorts (needs `onSortChange`). */
  sortable?: boolean;
  /**
   * One line with an ellipsis and the full text as tooltip; the column takes
   * the space the others leave (e.g. the items of a sale).
   */
  truncate?: boolean;
  /** CSS width, e.g. `'8rem'`. */
  width?: string;
  /** The row's title: first column, slightly stronger, link target. */
  primary?: boolean;
  /**
   * Place in the phone list: `subtitle` (grey line under the title),
   * `trailing` (right) or `hidden`. Defaults: right-aligned columns become
   * `trailing`, the first two others `subtitle`, the rest `hidden`.
   */
  mobile?: 'subtitle' | 'trailing' | 'hidden';
  /**
   * A badge behind the cell content, outside the row's link (e.g. a status
   * that only a few rows have); shown on phones behind the title, too.
   */
  badge?: (row: T) => ReactNode;
  /** Content in the phone list if it differs from `cell` (one line, no `CellText`). */
  mobileCell?: (row: T) => ReactNode;
}

export interface DataTableProps<T> {
  /** Table caption (accessible name); usually visually hidden. */
  caption: string;
  captionHidden?: boolean;
  columns: DataTableColumn<T>[];
  rows: T[];
  rowKey: (row: T) => string;
  /** Makes each row a link to its detail page (docs/ux-richtlinien.md §6.1). */
  rowHref?: (row: T) => string | undefined;
  /** Current sorting; without it the table keeps its own (starting at `defaultSort`). */
  sort?: TableSort | null;
  onSortChange?: (key: string, direction: SortDirection) => void;
  /** Starting sort when the table keeps its own state; unset keeps the given order. */
  defaultSort?: TableSort | undefined;
  /**
   * The rows come sorted by `sort` (e.g. from the server, for lists loaded
   * page by page, #188): headers still change the sort, the table keeps the
   * order of `rows`.
   */
  presorted?: boolean;
  /** Summary row under the table, by column key (e.g. the total of the sales shown). */
  footer?: Partial<Record<string, ReactNode>> | undefined;
  /**
   * `table` keeps the table on phones, too: for matrices with a few narrow
   * columns of controls (role permissions, notification channels).
   */
  layout?: 'responsive' | 'table';
  /** Row height: `regular` (44 px, default) or `compact` (32 px, like macOS lists). */
  density?: 'regular' | 'compact';
  /** Shown instead of rows when there are none (e.g. an `EmptyState`). */
  empty?: ReactNode;
  /** Shows placeholder rows. */
  loading?: boolean;
  /** Called when the pointer or focus enters a table row, `null` when it leaves. */
  onRowHover?: (row: T | null) => void;
  /**
   * The `…` menu at the end of each row (#197): frequent actions without
   * opening the row. Right-click, long press and the context-menu key open
   * it, too. Destructive items go last.
   */
  rowActions?: {
    /** Accessible name of the column, e.g. "Aktionen". */
    header: string;
    /** Accessible name of a row's button, e.g. "Weitere Aktionen: Anni". */
    label: (row: T) => string;
    items: (row: T) => (MenuItem | false | null | undefined)[];
  };
  /** Adds a checkbox column for selecting rows (bulk actions). */
  selection?: {
    selected: ReadonlySet<string>;
    onChange: (selected: Set<string>) => void;
    /** Accessible label of a row's checkbox, e.g. its name. */
    rowLabel: (row: T) => string;
    /** Accessible label of the "select all" checkbox. */
    allLabel: string;
  };
}

type Placement = 'title' | 'subtitle' | 'trailing' | 'hidden';

function placements<T>(columns: DataTableColumn<T>[]): Placement[] {
  const titleIndex = Math.max(
    0,
    columns.findIndex((c) => c.primary),
  );
  let subtitles = 0;
  let trailing = 0;
  return columns.map((c, i) => {
    if (i === titleIndex) return 'title';
    if (c.mobile) return c.mobile;
    if (c.align === 'end') return trailing++ === 0 ? 'trailing' : 'hidden';
    return subtitles++ < 2 ? 'subtitle' : 'hidden';
  });
}

const isEmpty = (v: ReactNode) =>
  v === null || v === undefined || v === false || v === '' || v === '–';

const ISO_DATE = /^\d{4}-\d{2}-\d{2}/;

const collator = new Intl.Collator('de', { numeric: true, sensitivity: 'base' });

/** Stable sort by the column's value; empty values stay last in both directions. */
function sortRows<T>(rows: T[], column: DataTableColumn<T> | undefined, direction: SortDirection) {
  const value = column?.sortValue;
  if (!value) return rows;
  const factor = direction === 'ascending' ? 1 : -1;
  return rows
    .map((row, index) => ({ row, index, v: value(row) }))
    .sort((a, b) => {
      const ae = a.v === null || a.v === undefined || a.v === '';
      const be = b.v === null || b.v === undefined || b.v === '';
      if (ae || be) return ae === be ? a.index - b.index : ae ? 1 : -1;
      const r =
        typeof a.v === 'number' && typeof b.v === 'number'
          ? a.v - b.v
          : collator.compare(String(a.v), String(b.v));
      return r === 0 ? a.index - b.index : r * factor;
    })
    .map((e) => e.row);
}

/** Plain text of a cell for the tooltip of a truncated cell. */
function textOf(node: ReactNode): string {
  if (node === null || node === undefined || typeof node === 'boolean') return '';
  if (typeof node === 'string' || typeof node === 'number') return String(node);
  if (Array.isArray(node)) return node.map(textOf).join('');
  if (isValidElement<{ children?: ReactNode }>(node)) return textOf(node.props.children);
  return '';
}

/**
 * First direction of a column (§6.4): amounts, quantities and dates start
 * descending, text ascending.
 */
export function firstSortDirection<T>(
  column: DataTableColumn<T>,
  rows: readonly T[],
): SortDirection {
  if (column.align === 'end') return 'descending';
  // The first filled value tells the kind; empty cells say nothing.
  let first: unknown;
  for (const row of rows) {
    first = column.sortValue?.(row);
    if (first !== null && first !== undefined && first !== '') break;
  }
  return typeof first === 'number' || (typeof first === 'string' && ISO_DATE.test(first))
    ? 'descending'
    : 'ascending';
}

/** The toolbar's sort menu for phones, where tables have no headers (§6.4). */
export interface SortMenu {
  label: string;
  value: string;
  options: { value: string; label: string }[];
  onChange: (value: string) => void;
  phoneOnly: true;
}

/**
 * Sort menu built from a table's sortable columns (§6.4, #188): "Standard"
 * resets to the list's default order, every column is an option with a
 * check mark; choosing the checked column again reverses its direction.
 * Values: `key` ascending, `-key` descending, `''` default.
 */
export function columnSortMenu<T>({
  columns,
  rows,
  sort,
  onSortChange,
  onReset,
  label,
  defaultLabel,
}: {
  columns: readonly DataTableColumn<T>[];
  rows: readonly T[];
  sort: TableSort | null;
  onSortChange: (key: string, direction: SortDirection) => void;
  onReset: () => void;
  /** e.g. "Sortieren". */
  label: string;
  /** e.g. "Standard". */
  defaultLabel: string;
}): SortMenu {
  const encode = (key: string, direction: SortDirection) =>
    direction === 'descending' ? `-${key}` : key;
  const value = sort ? encode(sort.key, sort.direction) : '';
  const sortable = columns.filter((c) => c.sortValue || c.sortable);
  return {
    label,
    value,
    phoneOnly: true,
    options: [
      { value: '', label: defaultLabel },
      ...sortable.map((c) => {
        const active = sort?.key === c.key;
        return {
          value: active ? value : encode(c.key, firstSortDirection(c, rows)),
          label: active ? `${c.header} ${sort.direction === 'descending' ? '▼' : '▲'}` : c.header,
        };
      }),
    ],
    onChange: (v) => {
      if (v === '') return onReset();
      const descending = v.startsWith('-');
      const key = descending ? v.slice(1) : v;
      // The checked option again reverses the direction (like iOS Files).
      if (v === value) {
        onSortChange(key, descending ? 'ascending' : 'descending');
      } else {
        onSortChange(key, descending ? 'descending' : 'ascending');
      }
    },
  };
}

/**
 * Whether the table is wider than its card (#188): many columns at
 * 768–1100 px. The card then scrolls sideways instead of cutting off the
 * last columns and the row menu; only then the header does not stick.
 */
function useTooWide(): [(el: HTMLDivElement | null) => void, boolean] {
  const [wrapper, setWrapper] = useState<HTMLDivElement | null>(null);
  const [tooWide, setTooWide] = useState(false);
  useEffect(() => {
    if (!wrapper || typeof ResizeObserver === 'undefined') return;
    const table = wrapper.querySelector('table');
    if (!table) return;
    // The table's own width does not depend on the wrapper's overflow, so
    // switching it cannot flip the result back.
    const check = () => setTooWide(table.offsetWidth > wrapper.clientWidth + 1);
    const observer = new ResizeObserver(check);
    observer.observe(wrapper);
    observer.observe(table);
    check();
    return () => observer.disconnect();
  }, [wrapper]);
  return [setWrapper, tooWide];
}

/** Next sort when a header is clicked: numbers and dates start descending (§6.4). */
function nextDirection(current: SortDirection | undefined, numeric: boolean): SortDirection {
  if (current === undefined) return numeric ? 'descending' : 'ascending';
  return current === 'ascending' ? 'descending' : 'ascending';
}

/**
 * Data table on wide screens, iOS-style list on phones (title, grey subtitle,
 * value on the right, chevron). Whole rows link to the detail page, ↑/↓
 * move between rows. Columns with `sortValue` sort on a click on their header
 * (controlled via `sort`/`onSortChange`, or by the table itself); on phones
 * the `ListToolbar` offers sorting. Empty cells show a grey "–".
 */
export function DataTable<T>({
  caption,
  captionHidden = true,
  columns,
  rows: givenRows,
  rowKey,
  rowHref,
  sort: controlledSort,
  onSortChange,
  defaultSort,
  presorted = false,
  footer,
  density = 'regular',
  empty,
  loading = false,
  onRowHover,
  selection,
  rowActions,
  layout = 'responsive',
}: DataTableProps<T>) {
  const compact = useMediaQuery(compactQuery) && layout === 'responsive';
  const [wrapperRef, tooWide] = useTooWide();
  const Link = useLinkComponent();
  const [ownSort, setOwnSort] = useState<TableSort | null>(defaultSort ?? null);
  const sort = controlledSort !== undefined ? controlledSort : ownSort;
  const changeSort = (key: string, direction: SortDirection) => {
    if (controlledSort === undefined) setOwnSort({ key, direction });
    onSortChange?.(key, direction);
  };
  const rows =
    sort && !presorted
      ? sortRows(
          givenRows,
          columns.find((c) => c.key === sort.key),
          sort.direction,
        )
      : givenRows;
  const keys = rows.map(rowKey);
  const selectedCount = selection ? keys.filter((k) => selection.selected.has(k)).length : 0;
  const allSelected = keys.length > 0 && selectedCount === keys.length;
  const toggle = (key: string) => {
    if (!selection) return;
    const next = new Set(selection.selected);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    selection.onChange(next);
  };
  const titleCell = (row: T, content: ReactNode) => {
    const href = rowHref?.(row);
    return href ? (
      <Link href={href} className={styles.rowLink} transition="push">
        {content}
      </Link>
    ) : (
      content
    );
  };

  /**
   * A click in a linked table row follows the row's link (#83), unless it hit
   * a control, a link of its own or an overlay of the row (its menu or a
   * dialog, which sit inside the row in the DOM), used a modifier key or ended a text selection.
   */
  const followRowLink = (e: MouseEvent<HTMLTableRowElement>) => {
    const target = e.target as Element;
    if (
      e.defaultPrevented ||
      e.button !== 0 ||
      e.metaKey ||
      e.ctrlKey ||
      e.shiftKey ||
      e.altKey ||
      !e.currentTarget.contains(target) ||
      target.closest(INTERACTIVE) ||
      target.closest(OVERLAY) ||
      (window.getSelection()?.toString() ?? '') !== ''
    ) {
      return;
    }
    e.currentTarget.querySelector<HTMLElement>(`.${styles.rowLink}`)?.click();
  };

  /** ↑/↓ move the focus to the link of the previous or next row (like macOS lists). */
  const moveFocus = (e: KeyboardEvent<HTMLTableSectionElement>) => {
    if ((e.key !== 'ArrowDown' && e.key !== 'ArrowUp') || e.altKey || e.metaKey || e.ctrlKey) {
      return;
    }
    const current = (e.target as Element).closest('tr');
    if (!current || !(e.target as Element).matches(`.${styles.rowLink}`)) return;
    let row = e.key === 'ArrowDown' ? current.nextElementSibling : current.previousElementSibling;
    while (row && !row.querySelector(`.${styles.rowLink}`)) {
      row = e.key === 'ArrowDown' ? row.nextElementSibling : row.previousElementSibling;
    }
    const link = row?.querySelector<HTMLElement>(`.${styles.rowLink}`);
    if (!link) return;
    e.preventDefault();
    // Focusing scrolls the row into view; `scroll-margin` keeps it clear of
    // the sticky header.
    link.focus();
  };

  /** Amounts, quantities and dates sort descending on the first click (§6.4). */
  const startsDescending = (c: DataTableColumn<T>) =>
    firstSortDirection(c, givenRows) === 'descending';

  const sortableColumn = (c: DataTableColumn<T>) =>
    Boolean(c.sortValue) || (Boolean(c.sortable) && Boolean(onSortChange));

  const renderCell = (c: DataTableColumn<T>, row: T) => {
    const value = c.cell(row);
    if (isEmpty(value)) {
      const dash = <span className={styles.placeholder}>–</span>;
      return c.primary ? titleCell(row, dash) : dash;
    }
    const linked = c.primary ? titleCell(row, value) : value;
    const content = c.truncate ? (
      <span className={styles.truncated} title={textOf(value) || undefined}>
        {linked}
      </span>
    ) : (
      linked
    );
    const badge = c.badge?.(row);
    if (isEmpty(badge)) return content;
    return (
      <span className={styles.withBadge}>
        {content}
        {badge}
      </span>
    );
  };

  if (!loading && rows.length === 0 && empty) {
    return <div className={styles.empty}>{empty}</div>;
  }

  if (compact) {
    const place = placements(columns);
    // The footer (count, sums) as one grey line under the list.
    const summary =
      footer && !loading && rows.length > 0
        ? columns.map((c) => footer[c.key]).filter((v) => !isEmpty(v))
        : [];
    return (
      <>
        <ul className={styles.list} aria-label={caption}>
          {loading &&
            Array.from({ length: 3 }, (_, i) => (
              <li key={`skeleton-${i}`} className={styles.item}>
                <span className={styles.itemText}>
                  <Skeleton width="55%" />
                  <Skeleton width="35%" />
                </span>
              </li>
            ))}
          {!loading &&
            rows.map((row) => {
              const key = rowKey(row);
              const subtitle = columns
                .map((c, i) => (place[i] === 'subtitle' ? (c.mobileCell ?? c.cell)(row) : null))
                .filter((v) => !isEmpty(v));
              const trailing = columns.find((_, i) => place[i] === 'trailing');
              const title = columns.find((_, i) => place[i] === 'title');
              const href = rowHref?.(row);
              return (
                <ListItem
                  key={key}
                  linked={Boolean(href)}
                  actions={rowActions ? orderRowActions(rowActions.items(row)) : []}
                  actionsLabel={rowActions?.label(row) ?? ''}
                >
                  {selection && (
                    <span className={styles.itemSelect}>
                      <Checkbox
                        aria-label={selection.rowLabel(row)}
                        checked={selection.selected.has(key)}
                        onChange={() => toggle(key)}
                      />
                    </span>
                  )}
                  <span className={styles.itemText}>
                    <span className={styles.itemTitle}>
                      {title && titleCell(row, title.cell(row))}
                      {title?.badge && !isEmpty(title.badge(row)) && <> {title.badge(row)}</>}
                    </span>
                    {subtitle.length > 0 && (
                      <span className={styles.itemSubtitle}>
                        {subtitle.map((v, i) => (
                          <Fragment key={i}>
                            {i > 0 && ' · '}
                            {v}
                          </Fragment>
                        ))}
                      </span>
                    )}
                  </span>
                  {trailing && (
                    <span className={styles.itemTrailing}>
                      {(trailing.mobileCell ?? trailing.cell)(row)}
                    </span>
                  )}
                </ListItem>
              );
            })}
        </ul>
        {summary.length > 0 && (
          <p className={styles.listFooter}>
            {summary.map((v, i) => (
              <Fragment key={i}>
                {i > 0 && ' · '}
                {v}
              </Fragment>
            ))}
          </p>
        )}
      </>
    );
  }

  return (
    <div
      ref={wrapperRef}
      className={[styles.wrapper, tooWide && styles.scrolls].filter(Boolean).join(' ')}
    >
      <table
        className={[styles.table, density === 'compact' && styles.compact]
          .filter(Boolean)
          .join(' ')}
      >
        <caption className={captionHidden ? styles.hiddenCaption : styles.caption}>
          {caption}
        </caption>
        <thead>
          <tr>
            {selection && (
              <th scope="col" className={styles.select}>
                <Checkbox
                  aria-label={selection.allLabel}
                  checked={allSelected}
                  ref={(el) => {
                    if (el) el.indeterminate = selectedCount > 0 && !allSelected;
                  }}
                  onChange={() => selection.onChange(allSelected ? new Set() : new Set(keys))}
                />
              </th>
            )}
            {columns.map((c) => {
              const sorted = sort?.key === c.key ? sort.direction : undefined;
              const sortable = sortableColumn(c);
              return (
                <th
                  key={c.key}
                  scope="col"
                  className={
                    [
                      c.align === 'end' && styles.end,
                      c.align === 'center' && styles.center,
                      c.truncate && styles.flexible,
                    ]
                      .filter(Boolean)
                      .join(' ') || undefined
                  }
                  style={c.width ? { width: c.width } : undefined}
                  {...(sortable ? { 'aria-sort': sorted ?? 'none' } : {})}
                >
                  {sortable ? (
                    <button
                      type="button"
                      className={[styles.sort, sorted && styles.sorted].filter(Boolean).join(' ')}
                      onClick={() => changeSort(c.key, nextDirection(sorted, startsDescending(c)))}
                    >
                      {c.header}
                      <Icon
                        name={
                          (sorted ?? (startsDescending(c) ? 'descending' : 'ascending')) ===
                          'ascending'
                            ? 'chevron-up'
                            : 'chevron-down'
                        }
                        size="xs"
                        className={styles.sortIcon}
                      />
                    </button>
                  ) : c.hideHeader ? (
                    <span className={styles.hiddenCaption}>{c.header}</span>
                  ) : (
                    c.header
                  )}
                </th>
              );
            })}
            {rowActions && (
              <th scope="col" className={styles.actions}>
                <span className={styles.hiddenCaption}>{rowActions.header}</span>
              </th>
            )}
          </tr>
        </thead>
        {/* Delegates ↑/↓ from the row links inside; the rows stay plain rows. */}
        {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions */}
        <tbody onKeyDown={moveFocus}>
          {loading &&
            Array.from({ length: 3 }, (_, i) => (
              <tr key={`skeleton-${i}`} className={styles.row}>
                {selection && <td className={styles.select} />}
                {columns.map((c) => (
                  <td key={c.key}>
                    <Skeleton width={c.primary ? '60%' : '40%'} />
                  </td>
                ))}
                {rowActions && <td className={styles.actions} />}
              </tr>
            ))}
          {!loading &&
            rows.map((row) => {
              const key = rowKey(row);
              const linked = Boolean(rowHref?.(row));
              return (
                <TableRow
                  key={key}
                  linked={linked}
                  selected={Boolean(selection?.selected.has(key))}
                  onClick={followRowLink}
                  actions={rowActions ? orderRowActions(rowActions.items(row)) : null}
                  actionsLabel={rowActions?.label(row) ?? ''}
                  {...(onRowHover
                    ? {
                        hover: {
                          onPointerEnter: () => onRowHover(row),
                          onPointerLeave: () => onRowHover(null),
                          onFocus: () => onRowHover(row),
                          onBlur: () => onRowHover(null),
                        },
                      }
                    : {})}
                >
                  {selection && (
                    <td className={styles.select}>
                      <Checkbox
                        aria-label={selection.rowLabel(row)}
                        checked={selection.selected.has(key)}
                        onChange={() => toggle(key)}
                      />
                    </td>
                  )}
                  {columns.map((c) => (
                    <td
                      key={c.key}
                      className={
                        [
                          c.align === 'end' && styles.end,
                          c.align === 'center' && styles.center,
                          c.primary && styles.primary,
                          c.truncate && styles.flexible,
                        ]
                          .filter(Boolean)
                          .join(' ') || undefined
                      }
                    >
                      {renderCell(c, row)}
                    </td>
                  ))}
                </TableRow>
              );
            })}
        </tbody>
        {footer && !loading && rows.length > 0 && (
          <tfoot>
            <tr>
              {selection && <td className={styles.select} />}
              {columns.map((c) => (
                <td key={c.key} className={c.align === 'end' ? styles.end : undefined}>
                  {footer[c.key]}
                </td>
              ))}
              {rowActions && <td className={styles.actions} />}
            </tr>
          </tfoot>
        )}
      </table>
    </div>
  );
}

interface TableRowProps {
  linked: boolean;
  selected: boolean;
  onClick: (e: MouseEvent<HTMLTableRowElement>) => void;
  /** `null`: the table has no actions column; `[]`: this row has no actions. */
  actions: MenuItem[] | null;
  actionsLabel: string;
  hover?: {
    onPointerEnter: () => void;
    onPointerLeave: () => void;
    onFocus: () => void;
    onBlur: () => void;
  };
  children: ReactNode;
}

/**
 * One table row. The row's link is in the title cell for keyboard and screen
 * readers; the click handler only extends it to the whole row.
 */
function TableRow({
  linked,
  selected,
  onClick,
  actions,
  actionsLabel,
  hover,
  children,
}: TableRowProps) {
  const menu = useRowActions(Boolean(actions?.length));
  return (
    <tr
      className={[styles.row, linked && styles.linked, selected && styles.selected]
        .filter(Boolean)
        .join(' ')}
      {...(linked ? { onClick } : {})}
      {...(actions?.length ? menu.rowProps : {})}
      {...hover}
    >
      {children}
      {actions && (
        <td className={styles.actions}>
          <RowActions label={actionsLabel} items={actions} state={menu} />
        </td>
      )}
    </tr>
  );
}

/** One item of the phone list; with actions, the `…` takes the chevron's place. */
function ListItem({
  linked,
  actions,
  actionsLabel,
  children,
}: {
  linked: boolean;
  actions: MenuItem[];
  actionsLabel: string;
  children: ReactNode;
}) {
  const menu = useRowActions(actions.length > 0);
  return (
    <li
      className={[styles.item, linked && styles.itemLink].filter(Boolean).join(' ')}
      {...(actions.length > 0 ? menu.rowProps : {})}
    >
      {children}
      {actions.length > 0 ? (
        <RowActions label={actionsLabel} items={actions} state={menu} />
      ) : (
        linked && <Icon name="chevron-right" size="sm" className={styles.chevron} />
      )}
    </li>
  );
}

/** A cell value with a grey second line, e.g. "Ameisensäure" / "01.08.2026". */
export function CellText({ main, sub }: { main: ReactNode; sub?: ReactNode }) {
  return (
    <span className={styles.cellText}>
      <span>{main}</span>
      {sub !== undefined && sub !== null && sub !== '' && (
        <span className={styles.cellSub}>{sub}</span>
      )}
    </span>
  );
}
